// src/components/registration/steps/supplierSteps/SupplierStep2ApprovalGate.jsx

import React from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";

const SupplierStep2ApprovalGate = ({
  setCardStep,
  companyStructure,
  suppliersSystem,
  onChange,
  finishFlow,
}) => {
  // Provide fallbacks for approvalGate properties
  const approvalGate = suppliersSystem?.approvalGate || {};
  const isApprovalRequired = approvalGate.isRequired || false;
  const needRoleLevel = approvalGate.needRoleLevel || false; // new toggle

  // Group-based data
  const selectedApproverGroups = approvalGate.approverGroups || [];

  // Role-based data
  const selectedApproverRoles = approvalGate.approverRoles || [];

  // 1) Toggle master "Require Approval"
  const handleApprovalRequiredCheckbox = (e) => {
    const newIsRequired = e.target.checked;
    if (!newIsRequired) {
      onChange({
        ...suppliersSystem,
        approvalGate: {
          // reset everything if turning off
          isRequired: false,
          needRoleLevel: false,
          approverGroups: [],
          approverRoles: [],
        },
      });
    } else {
      onChange({
        ...suppliersSystem,
        approvalGate: {
          ...approvalGate,
          isRequired: true,
        },
      });
    }
  };

  // 2) Toggle group-based vs. role-based
  const handleNeedRoleLevel = (e) => {
    const newVal = e.target.checked;
    if (newVal) {
      // switch to role-based => clear groups
      onChange({
        ...suppliersSystem,
        approvalGate: {
          ...approvalGate,
          needRoleLevel: true,
          approverGroups: [],
        },
      });
    } else {
      // revert to group-based => clear roles
      onChange({
        ...suppliersSystem,
        approvalGate: {
          ...approvalGate,
          needRoleLevel: false,
          approverRoles: [],
        },
      });
    }
  };

  // 3) Group-based check
  const handleCheckboxForGroup = (groupName) => {
    let newArr;
    if (selectedApproverGroups.includes(groupName)) {
      newArr = selectedApproverGroups.filter((g) => g !== groupName);
    } else {
      newArr = [...selectedApproverGroups, groupName];
    }
    onChange({
      ...suppliersSystem,
      approvalGate: {
        ...approvalGate,
        approverGroups: newArr,
      },
    });
  };

  // 4) Role-based check
  const handleCheckboxForRole = (roleKey) => {
    let newArr;
    if (selectedApproverRoles.includes(roleKey)) {
      newArr = selectedApproverRoles.filter((r) => r !== roleKey);
    } else {
      newArr = [...selectedApproverRoles, roleKey];
    }
    onChange({
      ...suppliersSystem,
      approvalGate: {
        ...approvalGate,
        approverRoles: newArr,
      },
    });
  };

  // Flatten roles
  const allRoles = (companyStructure?.userGroups || []).flatMap((g) =>
    g.roles.map((r) => ({
      groupName: g.groupName,
      roleName: r.roleName,
    }))
  );

  const getRoleKey = (groupName, roleName) => `${groupName}||${roleName}`;

  // 5) Validation
  const handleContinue = () => {
    if (isApprovalRequired) {
      if (!needRoleLevel) {
        // group-based => must have at least one group
        if (selectedApproverGroups.length === 0) {
          alert(
            "Please select at least one user group for supplier approvals."
          );
          return;
        }
      } else {
        // role-based => must have at least one role
        if (selectedApproverRoles.length === 0) {
          alert("Please select at least one user role for supplier approvals.");
          return;
        }
      }
    }
    finishFlow();
  };

  return (
    <div>
      <h2 className="card-title">Supplier Approval Gate</h2>
      <p className="card-subtitle">
        By default, no approval is required to register or manage suppliers. If
        you want an approval process, check the box below and select which user
        groups or roles can approve supplier-related actions.
      </p>

      {/* Master "Require Approval?" */}
      <div className="role-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={isApprovalRequired}
            onChange={handleApprovalRequiredCheckbox}
          />
          Require Approval for supplier actions?
        </label>
      </div>

      {/* If approval is required, show the group vs. role toggles */}
      {isApprovalRequired && (
        <div className="approval-frame">
          <p className="approval-title">
            Which user groups or roles will have approval rights for suppliers?
          </p>

          {/* Toggle between group-based vs. role-based */}
          <div className="role-toggle-row">
            <label>
              <input
                type="checkbox"
                checked={needRoleLevel}
                onChange={handleNeedRoleLevel}
              />
              Need Role-Level Approval?
            </label>
          </div>

          {!needRoleLevel ? (
            // Group-based
            <>
              <p className="approval-title">
                Please select user groups for supplier approval:
              </p>
              {companyStructure?.userGroups?.map((g) => (
                <label key={g.groupName} className="approval-group-label">
                  <input
                    type="checkbox"
                    checked={selectedApproverGroups.includes(g.groupName)}
                    onChange={() => handleCheckboxForGroup(g.groupName)}
                  />
                  {g.groupName}
                </label>
              ))}
            </>
          ) : (
            // Role-based
            <>
              <p className="approval-title">
                Please select user roles for supplier approval:
              </p>
              {allRoles.length === 0 ? (
                <p>
                  No roles found. Ensure your companyStructure includes
                  userGroups and roles.
                </p>
              ) : (
                allRoles.map((rObj) => {
                  const roleKey = getRoleKey(rObj.groupName, rObj.roleName);
                  const isChecked = selectedApproverRoles.includes(roleKey);
                  return (
                    <label key={roleKey} className="approval-group-label">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxForRole(roleKey)}
                      />
                      {rObj.groupName} - {rObj.roleName}
                    </label>
                  );
                })
              )}
            </>
          )}
        </div>
      )}

      <div className="continue-button-container">
        <MultiStyleButton
          label="Back"
          buttonType="cancel"
          onClick={() => setCardStep(1)}
        />
        <MultiStyleButton
          label="Finish"
          buttonType="approve"
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};

SupplierStep2ApprovalGate.propTypes = {
  setCardStep: PropTypes.func.isRequired,
  companyStructure: PropTypes.object,
  suppliersSystem: PropTypes.shape({
    approvalGate: PropTypes.shape({
      isRequired: PropTypes.bool,
      needRoleLevel: PropTypes.bool,
      approverGroups: PropTypes.arrayOf(PropTypes.string),
      approverRoles: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  finishFlow: PropTypes.func.isRequired,
};

export default SupplierStep2ApprovalGate;
